// Modal.tsx — Three-slot dialog surface bound to design tokens.
import React, { useEffect } from 'react';

export interface ModalProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
    isOpen: boolean;
    onClose: () => void;
    width?: string;
}

const SLOT = Symbol('ModalSlot');

type SlotKind = 'header' | 'body' | 'footer';

interface SlotComponent<P = React.HTMLAttributes<HTMLDivElement>> extends React.FC<P> {
    [SLOT]?: SlotKind;
}

interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    onClose?: () => void;
    showClose?: boolean;
}

const slotPadding = '0.75rem 1rem';

const headerStyle: React.CSSProperties = {
    padding: slotPadding,
    borderBottom: '1px solid var(--color-border-subtle)',
    font: '700 var(--type-title-size)/var(--type-title-line-height) var(--font-mono)',
    color: 'var(--color-text-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-sm)',
};

const bodyStyle: React.CSSProperties = {
    padding: slotPadding,
};

const footerStyle: React.CSSProperties = {
    padding: slotPadding,
    borderTop: '1px solid var(--color-border-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--space-sm)',
};

const closeBtnStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    font: '700 var(--type-title-size)/var(--type-title-line-height) var(--font-mono)',
    padding: '0 0.25em',
    lineHeight: 1,
};

const ModalHeader: SlotComponent<ModalHeaderProps> = ({ style, children, onClose, showClose = true, ...rest }) => (
    <div style={{ ...headerStyle, ...style }} {...rest}>
        <span style={{ flex: 1 }}>{children}</span>
        {showClose && onClose && (
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={closeBtnStyle}
            >
                ×
            </button>
        )}
    </div>
);
ModalHeader[SLOT] = 'header';
ModalHeader.displayName = 'Modal.Header';

const ModalBody: SlotComponent = ({ style, children, ...rest }) => (
    <div style={{ ...bodyStyle, ...style }} {...rest}>{children}</div>
);
ModalBody[SLOT] = 'body';
ModalBody.displayName = 'Modal.Body';

const ModalFooter: SlotComponent = ({ style, children, ...rest }) => (
    <div style={{ ...footerStyle, ...style }} {...rest}>{children}</div>
);
ModalFooter[SLOT] = 'footer';
ModalFooter.displayName = 'Modal.Footer';

const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-md)',
    zIndex: 1000,
};

const ModalRoot: React.FC<ModalProps> & {
    Header: typeof ModalHeader;
    Body: typeof ModalBody;
    Footer: typeof ModalFooter;
} = ({ isOpen, onClose, width, style, children, onClick, ...rest }) => {
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const dialogStyle: React.CSSProperties = {
        background: 'var(--color-bg-card)',
        color: 'var(--color-text-primary)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        width: width ?? 'min(480px, calc(100vw - var(--space-lg)))',
        overflow: 'hidden',
        ...style,
    };

    return (
        <div
            style={backdropStyle}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                role="dialog"
                aria-modal="true"
                style={dialogStyle}
                onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
                {...rest}
            >
                {children}
            </div>
        </div>
    );
};

ModalRoot.Header = ModalHeader;
ModalRoot.Body = ModalBody;
ModalRoot.Footer = ModalFooter;

export const Modal = ModalRoot;

export default Modal;
